import React from 'react';
import { formatPlatformName } from '../../utils/platformHelpers';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import {
  formatNumber,
  formatCurrency,
  formatPercent,
  formatWatchHours,
  getPlatformColor
} from '../../utils/formatters';
import YouTubeEstimateTooltip from '../YouTubeEstimateTooltip';

const PlatformBreakdown = ({ data, compact = false }) => {
  // Add colors to data
  const chartData = data.map(platform => ({
    ...platform,
    color: getPlatformColor(platform.platform)
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const item = payload[0].payload;

    return (
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4">
        <div className="flex items-center mb-2">
          <div 
            className="w-3 h-3 rounded-full mr-2"
            style={{ backgroundColor: item.color }}
          />
          <span className="font-medium text-gray-900">{formatPlatformName(item.platform)}</span>
        </div>
        <div className="space-y-1 text-sm">
          <p className="text-gray-600">Views: <span className="font-medium text-gray-900">{formatNumber(item.views)}</span></p>
          <p className="text-gray-600">Creators: <span className="font-medium text-gray-900">{item.creatorCount}</span></p>
          <p className="text-gray-600">Content: <span className="font-medium text-gray-900">{item.contentCount}</span></p>
        </div>
      </div>
    );
  };

  const renderPieLabel = ({ name, percent }) => {
    if (percent < 0.05) return null;
    return `${(percent * 100).toFixed(0)}%`;
  };

  if (compact) {
    return (
      <div className="card-pink-accent bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Platform Distribution</h3>
        
        <div className="flex items-center">
          <div className="w-1/2 h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey="views"
                  nameKey="platform"
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  label={renderPieLabel}
                  labelLine={false}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          
          <div className="w-1/2 space-y-2">
            {chartData.map(platform => (
              <div key={platform.platform} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div 
                    className="w-3 h-3 rounded-full mr-2"
                    style={{ backgroundColor: platform.color }}
                  />
                  <span className="text-sm text-gray-600">{formatPlatformName(platform.platform)}</span>
                </div>
                <span className="text-sm font-medium text-gray-900">
                  {formatNumber(platform.views)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card-pink-accent bg-white rounded-xl border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">Platform Breakdown</h3>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Pie Chart - Views Distribution */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-4">Views by Platform</h4>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey="views"
                  nameKey="platform"
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  label={renderPieLabel}
                  labelLine={false}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend 
                  formatter={(value) => <span className="text-gray-600">{formatPlatformName(value)}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Bar Chart - Engagement Comparison */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-4">Engagement by Platform</h4>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={chartData} 
                layout="vertical"
                margin={{ top: 5, right: 30, left: 60, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis type="number" tickFormatter={formatNumber} />
                <YAxis 
                  type="category" 
                  dataKey="platform" 
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                  tickFormatter={(value) => value.charAt(0).toUpperCase() + value.slice(1)}
                />
                <Tooltip 
                  formatter={(value) => formatNumber(value)}
                  labelFormatter={(label) => label.charAt(0).toUpperCase() + label.slice(1)}
                />
                <Bar dataKey="likes" name="Likes" fill="#EC4899" radius={[0, 4, 4, 0]} />
                <Bar dataKey="comments" name="Comments" fill="#F59E0B" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Platform Details Table */}
      <div className="mt-8">
        <h4 className="text-sm font-medium text-gray-700 mb-4">Platform Details</h4>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Platform</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Creators</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Content</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Views</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">% of Total</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Engagement</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  <div className="flex items-center justify-end gap-1.5">
                    <span>Eng. Rate</span>
                    <YouTubeEstimateTooltip metric="engagement" />
                  </div>
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Avg CCV</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  <div className="flex items-center justify-end gap-1.5">
                    <span>Watch Hours</span>
                    <YouTubeEstimateTooltip metric="watchHours" />
                  </div>
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Spend</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">CPV</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {chartData.map(platform => (
                <tr key={platform.platform} className="hover:bg-gray-50">
                  <td className="px-4 py-4">
                    <div className="flex items-center">
                      <div 
                        className="w-3 h-3 rounded-full mr-3"
                        style={{ backgroundColor: platform.color }}
                      />
                      <span className="font-medium text-gray-900">
                        {formatPlatformName(platform.platform)}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-right text-gray-600">
                    {platform.creatorCount}
                  </td>
                  <td className="px-4 py-4 text-right text-gray-600">
                    {platform.contentCount}
                  </td>
                  <td className="px-4 py-4 text-right font-medium text-gray-900">
                    {formatNumber(platform.views)}
                  </td>
                  <td className="px-4 py-4 text-right text-gray-600">
                    {platform.viewsPercentage}%
                  </td>
                  <td className="px-4 py-4 text-right text-gray-600">
                    {formatNumber(platform.likes + platform.comments + platform.shares)}
                  </td>
                  <td className="px-4 py-4 text-right">
                    <span className={platform.engagementRate > 3 ? 'text-green-600 font-medium' : 'text-gray-600'}>
                      {formatPercent(platform.engagementRate)}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-right text-gray-600">
                    {platform.avgCcv > 0 ? formatNumber(platform.avgCcv) : '-'}
                  </td>
                  <td className="px-4 py-4 text-right text-gray-600">
                    {platform.watchHours > 0 ? formatWatchHours(platform.watchHours) : '-'}
                  </td>
                  <td className="px-4 py-4 text-right font-medium text-gray-900">
                    {formatCurrency(platform.spend)}
                  </td>
                  <td className="px-4 py-4 text-right text-gray-600">
                    {formatCurrency(platform.costPerView)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50 font-medium">
                <td className="px-4 py-3 text-gray-900">Total</td>
                <td className="px-4 py-3 text-right text-gray-900">
                  {chartData.reduce((sum, p) => sum + p.creatorCount, 0)}
                </td>
                <td className="px-4 py-3 text-right text-gray-900">
                  {chartData.reduce((sum, p) => sum + p.contentCount, 0)}
                </td>
                <td className="px-4 py-3 text-right text-gray-900">
                  {formatNumber(chartData.reduce((sum, p) => sum + p.views, 0))}
                </td>
                <td className="px-4 py-3 text-right text-gray-900">100%</td>
                <td className="px-4 py-3 text-right text-gray-900">
                  {formatNumber(chartData.reduce((sum, p) => sum + p.likes + p.comments + p.shares, 0))}
                </td>
                <td className="px-4 py-3 text-right text-gray-900">-</td>
                <td className="px-4 py-3 text-right text-gray-900">-</td>
                <td className="px-4 py-3 text-right text-gray-900">
                  {formatWatchHours(chartData.reduce((sum, p) => sum + p.watchHours, 0))}
                </td>
                <td className="px-4 py-3 text-right text-gray-900">
                  {formatCurrency(chartData.reduce((sum, p) => sum + p.spend, 0))}
                </td>
                <td className="px-4 py-3 text-right text-gray-900">-</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PlatformBreakdown;
